import React, { useMemo, useCallback } from 'react';
import Button, { LinkButton } from '@cobalt/react-button';
import Flex from '@cobalt/react-flex';
import Icon from '@cobalt/react-icon';
import { useTheme } from '@cobalt/react-theme-provider';
import { Text } from '@cobalt/react-typography';
import { useViewport } from '@cobalt/react-viewport-provider';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const PLACEHOLDER = "…";
const generatePages = ({
  totalPages,
  currentPage,
  maxPagesCount
}) => {
  const safeTotalPages = Math.max(totalPages, 1);
  const totalPageArr = Array.from(Array(safeTotalPages), (value, index) => ++index);
  const sliceIndex = Math.floor(maxPagesCount / 2);
  const visiblePages = centeredWindowSlice(totalPageArr, currentPage - 1, sliceIndex);
  return fixedEdgesArray(visiblePages, safeTotalPages);
};

const fixedEdgesArray = (array, fixedEnd) => {
  const result = [...array];

  if (array[0] !== 1) {
    result[0] = 1;
    result[1] = PLACEHOLDER;
  }

  const lastPosition = array.length - 1;

  if (array[lastPosition] !== fixedEnd) {
    result[lastPosition] = fixedEnd;
    result[array.length - 2] = PLACEHOLDER;
  }

  return result;
};

const centeredWindowSlice = (array, center, sliceSize) => {
  return windowSlice(array, center - sliceSize, center + sliceSize);
};

const windowSlice = (array, unsafeStart, unsafeEnd) => {
  const maxEnd = array.length - 1;
  const prefix = unsafeEnd > maxEnd ? Math.abs(maxEnd - unsafeEnd) : 0;
  const suffix = unsafeStart < 0 ? Math.abs(unsafeStart) : 0;
  const safeStart = Math.max(0, unsafeStart - prefix);
  const safeEnd = Math.min(unsafeEnd + suffix, maxEnd);
  return array.slice(safeStart, safeEnd + 1);
};

const Pages = ({
  pages,
  currentPage,
  onPageClick
}) => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Flex, {
    gap: "1",
    alignY: "end"
  }, pages.map((page, index) => {
    const isSelected = page === currentPage;

    if (isSelected) {
      return /*#__PURE__*/React.createElement(Button, {
        key: index,
        "aria-current": "true",
        shape: "compact",
        type: "primary",
        size: "small"
      }, page);
    }

    return typeof page === "number" ? /*#__PURE__*/React.createElement(LinkButton, {
      key: index,
      type: "secondary",
      size: "small",
      tabIndex: 0,
      onClick: () => onPageClick(page),
      "data-testid": `pagination-page-${page}-button`
    }, /*#__PURE__*/React.createElement(Text, null, page)) : /*#__PURE__*/React.createElement(Flex, {
      key: index,
      padding: "1",
      alignY: "end",
      alignX: "center",
      width: "32px",
      height: "32px",
      "data-testid": `pagination-page-${page}-button`
    }, /*#__PURE__*/React.createElement(Text, {
      inline: true,
      color: theme.gray500
    }, page));
  }));
};

const Pagination = ({
  currentPage,
  totalPages,
  maxPagesCount,
  previousLabel = "Previous",
  nextLabel = "Next",
  onPageClick,
  onNextClick,
  onPreviousClick
}) => {
  const viewport = useViewport();
  const smallViewport = viewport === "small";
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;
  const pages = useMemo(() => generatePages({
    currentPage,
    totalPages,
    maxPagesCount
  }), [currentPage, totalPages, maxPagesCount]);
  const handleNextClick = useCallback(() => {
    if (!isLastPage) {
      onNextClick();
    }
  }, [isLastPage, onNextClick]);
  const handlePreviousClick = useCallback(() => {
    if (!isFirstPage) {
      onPreviousClick();
    }
  }, [isFirstPage, onPreviousClick]);
  const additionalPreviousProps = {};

  if (!isFirstPage) {
    additionalPreviousProps.tabIndex = 0;
  }

  const additionalNextProps = {};

  if (!isLastPage) {
    additionalNextProps.tabIndex = 0;
  }

  return /*#__PURE__*/React.createElement(Flex, {
    as: "nav",
    alignY: "center",
    gap: "1"
  }, /*#__PURE__*/React.createElement(LinkButton, _extends({
    type: "secondary",
    size: "small",
    "aria-label": previousLabel,
    disabled: isFirstPage,
    onClick: handlePreviousClick,
    "data-testid": "pagination-previous-button"
  }, additionalPreviousProps), /*#__PURE__*/React.createElement(Icon, {
    name: "chevron_left",
    size: "tiny"
  }), smallViewport ? null : /*#__PURE__*/React.createElement(Text, {
    inline: true
  }, previousLabel)), /*#__PURE__*/React.createElement(Pages, {
    pages: pages,
    currentPage: currentPage,
    onPageClick: onPageClick
  }), /*#__PURE__*/React.createElement(LinkButton, _extends({
    type: "secondary",
    size: "small",
    "aria-label": nextLabel,
    disabled: isLastPage,
    onClick: handleNextClick,
    "data-testid": "pagination-next-button"
  }, additionalNextProps), smallViewport ? null : /*#__PURE__*/React.createElement(Text, {
    inline: true
  }, nextLabel), /*#__PURE__*/React.createElement(Icon, {
    name: "chevron_right",
    size: "tiny"
  })));
};

export default Pagination;
